import React, { useMemo, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { Scale, Search, Loader2, BookOpen, Filter, X, BookmarkPlus, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";

const TOPIC_LABEL_FALLBACK = (tag) =>
  tag.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const KB_ALLOWED_TIERS = ["legal_pro", "business"];

export default function Jurisprudence() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQ);
  const [activeTopic, setActiveTopic] = useState(null);
  const [results, setResults] = useState([]);
  const [topics, setTopics] = useState([]);
  const [corpusSize, setCorpusSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // ruling_id → "saving" | "saved" — UI feedback after Save to KB
  const [savedIds, setSavedIds] = useState({});

  const canSaveToKB = user && KB_ALLOWED_TIERS.includes(user.subscription_tier);

  const runSearch = async (q, topic) => {
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams();
      // BM25 needs at least 2 chars — if user only picked a topic, search by the topic name
      params.set("q", (q || topic || "").trim() || "diritto");
      params.set("limit", "20");
      if (topic) params.set("topic", topic);
      const r = await api.get(`/legal/jurisprudence/search?${params.toString()}`);
      setResults(r.data.results || []);
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial: load topics + auto-run search if ?q= was passed (from inline
  // citation deep-link in chat).
  useImperativeLoad(() => {
    api.get("/legal/jurisprudence/topics").then((r) => {
      setTopics(r.data.topics || []);
      setCorpusSize(r.data.total_corpus_size || 0);
    });
    if (initialQ.trim()) {
      runSearch(initialQ, null);
    }
  }, [initialQ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    runSearch(query, activeTopic);
  };

  const handleTopicClick = (tag) => {
    const next = activeTopic === tag ? null : tag;
    setActiveTopic(next);
    runSearch(query, next);
  };

  const clearFilters = () => {
    setQuery("");
    setActiveTopic(null);
    setResults([]);
    setHasSearched(false);
  };

  const topTopics = useMemo(() => topics.slice(0, 12), [topics]);

  const handleSaveToKB = async (entry) => {
    if (!canSaveToKB) return;
    setSavedIds((s) => ({ ...s, [entry.id]: "saving" }));
    try {
      await api.post("/legal/knowledge", {
        title: entry.headline,
        body: entry.principle,
        source: "jurisprudence",
        source_ref: entry.id,
        citation: `${entry.court} n. ${entry.ruling_number} — ${entry.date}`,
        tags: entry.topic_tags || [],
      });
      setSavedIds((s) => ({ ...s, [entry.id]: "saved" }));
      toast.success(t("jurisprudence.kb_saved"));
    } catch (e) {
      setSavedIds((s) => {
        const { [entry.id]: _omit, ...rest } = s;
        return rest;
      });
      toast.error(t("jurisprudence.kb_save_failed"));
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="jurisprudence-page">
      <Seo
        title={t("seo.jurisprudence_title")}
        description={t("seo.jurisprudence_description")}
        canonicalPath="/legal/jurisprudence"
        ogImage="/brand/og-jurisprudence.png"
      />
      <Header />

      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-10">
        {/* Hero */}
        <div className="mb-12 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#D4A937]/30 bg-[#D4A937]/10 px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#A67C0F]">
            <Scale className="h-3 w-3" />
            {t("jurisprudence.hero_pretitle")}
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-[#05073B] sm:text-5xl">
            {t("jurisprudence.title")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            {t("jurisprudence.subtitle", { count: corpusSize })}
          </p>
        </div>

        {/* Search bar */}
        <form
          onSubmit={handleSubmit}
          className="mb-6 flex items-center gap-2 rounded-2xl border border-slate-300 bg-white p-2 shadow-sm focus-within:border-[#0B0E5C]"
          data-testid="jurisprudence-search-form"
        >
          <Search className="ml-3 h-5 w-5 shrink-0 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("jurisprudence.search_placeholder")}
            className="flex-1 border-0 bg-transparent text-base focus-visible:ring-0"
            data-testid="jurisprudence-search-input"
          />
          {(query || activeTopic) && (
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="clear"
              data-testid="jurisprudence-clear-btn"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <Button
            type="submit"
            disabled={loading || (!query.trim() && !activeTopic)}
            className="h-11 rounded-full bg-[#0B0E5C] px-6 font-bold text-white hover:bg-[#1A1D7A]"
            data-testid="jurisprudence-search-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("jurisprudence.search_btn")}
          </Button>
        </form>

        {/* Topic filter chips */}
        <div className="mb-10">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
            <Filter className="h-3 w-3" />
            {t("jurisprudence.filter_by_topic")}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="jurisprudence-topics">
            {topTopics.map(({ tag, count }) => {
              const active = activeTopic === tag;
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTopicClick(tag)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? "border-[#D4A937] bg-[#D4A937]/15 text-[#A67C0F]"
                      : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                  }`}
                  data-testid={`jurisprudence-topic-${tag}`}
                >
                  <span>
                    {t(`jurisprudence.topics.${tag}`, { _default: TOPIC_LABEL_FALLBACK(tag) })}
                  </span>
                  <span className={`text-[10px] ${active ? "text-[#A67C0F]/80" : "text-slate-400"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {!hasSearched ? (
            <Card className="border-dashed border-slate-300 bg-white/60 p-8 text-center" data-testid="jurisprudence-empty">
              <BookOpen className="mx-auto h-8 w-8 text-slate-400" />
              <p className="mt-3 font-medium text-slate-700">{t("jurisprudence.empty_title")}</p>
              <p className="mt-1 text-sm text-slate-500">{t("jurisprudence.empty_desc")}</p>
            </Card>
          ) : results.length === 0 && !loading ? (
            <Card className="p-8 text-center" data-testid="jurisprudence-no-results">
              <p className="text-sm text-slate-600">{t("jurisprudence.no_results")}</p>
            </Card>
          ) : (
            results.map((entry) => {
              const saveState = savedIds[entry.id];
              return (
              <Card
                key={entry.id}
                className="border-slate-200 bg-white p-6 transition-shadow hover:shadow-md"
                data-testid={`jurisprudence-result-${entry.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="rounded-full border border-[#0B0E5C]/20 bg-[#0B0E5C]/5 px-2.5 py-0.5 font-mono font-semibold text-[#0B0E5C]">
                        {entry.court}
                      </span>
                      <span className="font-mono text-slate-500">n. {entry.ruling_number}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">{entry.date}</span>
                    </div>
                    <h3 className="mt-2 font-display text-lg font-bold text-[#05073B]">
                      {entry.headline}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-700">
                      {entry.principle}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {entry.topic_tags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => handleTopicClick(tag)}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-[#D4A937]/15 hover:text-[#A67C0F]"
                        >
                          {t(`jurisprudence.topics.${tag}`, { _default: TOPIC_LABEL_FALLBACK(tag) })}
                        </button>
                      ))}
                    </div>
                  </div>
                  {canSaveToKB && (
                    <button
                      type="button"
                      onClick={() => handleSaveToKB(entry)}
                      disabled={saveState === "saving" || saveState === "saved"}
                      className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors disabled:cursor-default ${
                        saveState === "saved"
                          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                          : "border-[#D4A937]/40 bg-[#D4A937]/10 text-[#A67C0F] hover:border-[#D4A937] hover:bg-[#D4A937]/20"
                      }`}
                      data-testid={`jurisprudence-save-kb-${entry.id}`}
                      title={t("jurisprudence.save_to_kb")}
                    >
                      {saveState === "saving" ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : saveState === "saved" ? (
                        <BookmarkCheck className="h-3 w-3" />
                      ) : (
                        <BookmarkPlus className="h-3 w-3" />
                      )}
                      <span className="hidden sm:inline">{t("jurisprudence.save_to_kb")}</span>
                    </button>
                  )}
                </div>
              </Card>
              );
            })
          )}
        </div>

        {/* Footnote */}
        <p className="mt-12 rounded-xl border border-[#D4A937]/30 bg-[#D4A937]/10 p-4 text-xs leading-relaxed text-[#A67C0F]">
          <Scale className="mr-1 inline h-3 w-3" />
          <strong>{t("jurisprudence.disclaimer_title")}</strong>{" "}
          {t("jurisprudence.disclaimer_body")}
        </p>
      </section>

      <Footer />
    </div>
  );
}
